// ! Sentry

import type { ErrorEvent } from "@sentry/vue";
import type { App } from "vue";

import * as Sentry from "@sentry/vue";

import { appVersion } from "@/appVersion";
import { isLocalEnvironment } from "@/environment";

/**
 * Link a Sentry event to the LogRocket replay.
 *
 * The session URL is time-anchored, so it identifies the exact replay. It is a
 * tag (searchable/filterable) and a context (structured), not just `extra`.
 */
export const enrichEventWithLogRocket = (
  event: ErrorEvent,
  sessionURL: string | null,
): ErrorEvent => {
  if (sessionURL === null) {
    return event;
  }
  event.tags = { ...event.tags, "logrocket.session": sessionURL };
  event.contexts = { ...event.contexts, logrocket: { sessionURL } };
  return event;
};

export const reportException = (err: Error | unknown): void => {
  if (!isLocalEnvironment) {
    if (typeof err === "string") {
      Sentry.captureMessage(err);
    } else if (err instanceof Error) {
      Sentry.captureException(err);
    } else {
      Sentry.captureException(err);
    }
  }
};
export type SentryOptions = Parameters<typeof Sentry.init>[0];
export const registerSentry = (app: App, sentryOptions: SentryOptions = {}): typeof Sentry => {
  const SentryAppId = import.meta.env.VITE_SENTRY_ENV;
  if (SentryAppId) {
    // * The environment is the deploy axis (production / next / sandbox), not the
    //   DSN. `dist` carries the same value so sourcemaps and releases line up.
    const environment = import.meta.env.VITE_ENVIRONMENT ?? "production";
    Sentry.init({
      app,
      // * Custom Options
      ...sentryOptions,
      // * Default Options
      dsn: SentryAppId,
      environment,
      release: appVersion,
      dist: environment,
      beforeSend: sentryOptions?.beforeSend,
      integrations: (integrations: Extract<typeof sentryOptions.integrations, unknown[]>) => {
        integrations.push(Sentry.browserTracingIntegration());
        // * Custom
        if (Array.isArray(sentryOptions.integrations)) {
          for (const integration of sentryOptions.integrations) {
            integrations.push(integration);
          }
        } else if (sentryOptions.integrations) {
          integrations = sentryOptions.integrations(integrations);
        }
        return integrations;
      },
      // Performance Monitoring
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        "localhost",
        /^https:\/\/app-v3\.app\.blackfisk\.com\/api/,
        /^https:\/\/app-v3\.next\.blackfisk\.com\/api/,
        /^https:\/\/api\.next\.blackfisk\.com/,
        // * Custom
        ...(sentryOptions.tracePropagationTargets ?? []),
      ],
      // Session Replay
      // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      // beforeSend(event) {
      //   // Modify or drop the event here
      //   return event;
      // },
    });
  }
  return Sentry;
};
